// Closed, Deno-free contract shared by the browser and intake handler.
export const CALCULATOR_VERSION = "assessment-1";
export const FUNNEL_SCHEMA = "adiastra-funnel-v2";
export const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export type CampaignTouch = {
  campaign_id: string;
  campaign_revision: number;
  creative_id: string;
  creative_revision: number;
  manifest_hash: string;
  landing_path: string;
  source: "linkedin" | "direct" | "research" | "other";
  medium: "organic_social" | "none" | "moderated" | "other";
};
export type Contact = {
  name: string;
  email: string;
  company?: string;
  industry?: string;
};
type Base = {
  schema_version: 1;
  run_id: string;
  event_id: string;
  occurred_at: string;
  calculator_version: typeof CALCULATOR_VERSION;
};
export type IntakeRequest = Base &
  (
    | { action: "start"; context: CampaignTouch | null }
    | {
        action: "event";
        type: "assessment_completed" | "consultation_requested";
      }
    | { action: "contact"; contact: Contact }
  );
export type Receipt = {
  receipt_id: string;
  run_id: string;
  event_id: string;
  status: "recorded";
  attribution_status: string;
};

function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}
function keys(value: Record<string, unknown>, allowed: string[]) {
  return Object.keys(value).every((key) => allowed.includes(key));
}
function short(value: unknown, max: number, required = true): value is string {
  return (
    typeof value === "string" &&
    value.length <= max &&
    (!required || value.trim().length > 0) &&
    !/[\u0000-\u001f]/.test(value)
  );
}
export function isTouch(value: unknown): value is CampaignTouch {
  if (
    !record(value) ||
    !keys(value, [
      "campaign_id",
      "campaign_revision",
      "creative_id",
      "creative_revision",
      "manifest_hash",
      "landing_path",
      "source",
      "medium",
    ])
  )
    return false;
  return (
    typeof value.landing_path === "string" &&
    typeof value.source === "string" &&
    typeof value.medium === "string" &&
    short(value.campaign_id, 80) &&
    /^[a-z0-9-]+$/.test(value.campaign_id) &&
    short(value.creative_id, 80) &&
    /^[a-z0-9-]+$/.test(value.creative_id) &&
    Number.isInteger(value.campaign_revision) &&
    Number(value.campaign_revision) > 0 &&
    Number(value.campaign_revision) <= 10000 &&
    Number.isInteger(value.creative_revision) &&
    Number(value.creative_revision) > 0 &&
    Number(value.creative_revision) <= 10000 &&
    typeof value.manifest_hash === "string" &&
    /^[a-f0-9]{64}$/.test(value.manifest_hash) &&
    ["/med-spa-ai-automation", "/assessment", "/"].includes(
      value.landing_path as string,
    ) &&
    ["linkedin", "direct", "research", "other"].includes(
      value.source as string,
    ) &&
    ["organic_social", "none", "moderated", "other"].includes(
      value.medium as string,
    )
  );
}
export function isContact(value: unknown): value is Contact {
  return (
    record(value) &&
    keys(value, ["name", "email", "company", "industry"]) &&
    short(value.name, 120) &&
    short(value.email, 254) &&
    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value.email) &&
    (value.company === undefined || short(value.company, 160, false)) &&
    (value.industry === undefined || short(value.industry, 80, false))
  );
}
export function isIntakeRequest(value: unknown): value is IntakeRequest {
  if (
    !record(value) ||
    typeof value.run_id !== "string" ||
    typeof value.event_id !== "string"
  )
    return false;
  const common = [
    "schema_version",
    "run_id",
    "event_id",
    "occurred_at",
    "calculator_version",
    "action",
  ];
  if (
    value.schema_version !== 1 ||
    !UUID.test(value.run_id as string) ||
    !UUID.test(value.event_id as string) ||
    value.calculator_version !== CALCULATOR_VERSION ||
    typeof value.occurred_at !== "string" ||
    value.occurred_at.length > 30 ||
    !/^\d{4}-\d{2}-\d{2}T/.test(value.occurred_at) ||
    !Number.isFinite(Date.parse(value.occurred_at))
  )
    return false;
  if (value.action === "start")
    return (
      keys(value, [...common, "context"]) &&
      (value.context === null || isTouch(value.context))
    );
  if (value.action === "event")
    return (
      keys(value, [...common, "type"]) &&
      typeof value.type === "string" &&
      ["assessment_completed", "consultation_requested"].includes(
        value.type as string,
      )
    );
  return (
    value.action === "contact" &&
    keys(value, [...common, "contact"]) &&
    isContact(value.contact)
  );
}
export function isReceipt(
  value: unknown,
  request: IntakeRequest,
): value is Receipt {
  return (
    record(value) &&
    typeof value.receipt_id === "string" &&
    typeof value.attribution_status === "string" &&
    keys(value, [
      "receipt_id",
      "run_id",
      "event_id",
      "status",
      "attribution_status",
    ]) &&
    UUID.test(value.receipt_id) &&
    value.status === "recorded" &&
    value.run_id === request.run_id &&
    value.event_id === request.event_id &&
    ["registered", "direct_or_unknown", "unregistered_or_inactive"].includes(
      value.attribution_status as string,
    )
  );
}
